import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const PaymentAndInvoicing = () => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({
    availableBalance: 0,
    pendingBalance: 0,
    totalPaid: 0,
  });
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const formatCurrency = (value) => {
    const number = Number(value || 0);

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(number);
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/payments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setPayments([]);
        return;
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setPayments(data);
      } else if (Array.isArray(data?.payments)) {
        setPayments(data.payments);
      } else {
        setPayments([]);
      }

      if (data?.summary) {
        setSummary({
          availableBalance: data.summary.availableBalance || 0,
          pendingBalance: data.summary.pendingBalance || 0,
          totalPaid: data.summary.totalPaid || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment data:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusColor = (status) => {
    const normalized = String(status || '').toUpperCase();

    if (normalized === 'PAID' || normalized === 'COMPLETED') {
      return {
        backgroundColor: '#dcfce7',
        color: '#166534',
      };
    }

    if (normalized === 'PENDING' || normalized === 'PROCESSING') {
      return {
        backgroundColor: '#fef3c7',
        color: '#92400e',
      };
    }

    return {
      backgroundColor: '#e2e8f0',
      color: '#475569',
    };
  };

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Earnings & Payouts
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Review earnings, payout status, and payment history.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              backgroundColor: '#ffffff',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              Available Balance
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
              }}
            >
              {formatCurrency(summary.availableBalance)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              backgroundColor: '#ffffff',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              Pending
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
              }}
            >
              {formatCurrency(summary.pendingBalance)}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              backgroundColor: '#ffffff',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              Total Paid
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
              }}
            >
              {formatCurrency(summary.totalPaid)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              Payment History
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Completed and pending payouts will appear here.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="primary"
            onClick={fetchPayments}
            sx={{
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Refresh
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Loading payment history...
            </Typography>
          </Paper>
        ) : payments.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              No payment history available.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {payments.map((payment) => {
              const paymentId = payment.payment_id || payment.id;
              const status = payment.status || 'Pending';
              const statusColor = getStatusColor(status);

              return (
                <Grid item xs={12} key={paymentId}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#0f172a',
                            fontWeight: 700,
                            lineHeight: 1.35,
                          }}
                        >
                          {formatCurrency(payment.amount || payment.amount_usd)}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '0.92rem',
                          }}
                        >
                          {payment.created_at
                            ? new Date(payment.created_at).toLocaleDateString()
                            : 'Date not available'}
                        </Typography>
                      </Box>

                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          ...statusColor,
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>

                    {payment.description && (
                      <>
                        <Divider sx={{ my: 1.5 }} />

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            fontSize: '0.95rem',
                            lineHeight: 1.5,
                          }}
                        >
                          {payment.description}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </CourierLayout>
  );
};

export default PaymentAndInvoicing;